
import CallbackForm from '@/components/CallbackForm.vue';
import { CallbackInfo } from '@/models/callback-info';
import { Actions } from '@/store/actions';
import { computed, ComputedRef, defineComponent, onBeforeMount, ref, Ref } from 'vue';
import { useStore } from 'vuex';
import QuoteRequestForm from "@/components/QuoteRequestForm.vue";
import {createEmptyQuoteRequestInfo, QuoteRequestInfo} from "@/models/quote-request-info";

export default defineComponent({
  name: 'SummaryOfferCard',
    components: { QuoteRequestForm, CallbackForm },
  props: {
    calculatedPriceForDisplay: {
      type: String,
      required: false,
      default: ''
    },
    calculatedExternalPricePartForDisplay: {
      type: String,
      required: false,
      default: ''
    },
    calculatedInternalPricePartForDisplay: {
      type: String,
      required: false,
      default: ''
    },
    printContainerId: {
      type: String,
      required: false,
      default: ''
    }
  },
  setup() {
    const store = useStore();

    const quoteFormValid: Ref<boolean> = ref(false);
    const quoteRequestInfo: Ref<QuoteRequestInfo> = ref(createEmptyQuoteRequestInfo());

    /**
     * The callback information on how to contact
     * the user
     */
    const callbackFormData: Ref<CallbackInfo> = ref({} as CallbackInfo);

    /**
     * Indicates whether the callbackForm in the modal
     * is currently valid.
     */
    const isFormValid: Ref<boolean> = ref(false);

    /**
     * Returns whether there is currently a callback request in progress
     */
    const isCallbackRequestInProgress: ComputedRef<boolean> = computed(() => {
      return store.state.callbackRequest.isInProgress;
    });

    /**
     * Returns whether a callback request has finished.
     */
    const hasCallbackRequestFinished: ComputedRef<boolean> = computed(() => {
      return !store.state.callbackRequest.isInProgress
          && (store.state.callbackRequest.hasFailed
              || store.state.callbackRequest.hasSucceeded);
    });

    /**
     * Returns whether the current callback request has succeeded.
     */
    const hasCallbackRequestSucceeded: ComputedRef<boolean> = computed(() => {
      return !store.state.callbackRequest.hasFailed &&
          store.state.callbackRequest.hasSucceeded;
    });

    /**
     * Returns whether the current callback request has failed
     */
    const hasCallbackRequestFailed: ComputedRef<boolean> = computed(() => {
      return !store.state.callbackRequest.hasSucceeded
          && store.state.callbackRequest.hasFailed;
    });

    /**
     * Returns whether the quote request is currently underway.
     */
    const isQuoteRequestInProgress: ComputedRef<boolean> = computed(() => {
      return store.state.quoteRequest.isInProgress;
    });

    /**
     * Indicates true if the quote request has failed for any reason.
     */
    const hasQuoteRequestFailed: ComputedRef<boolean> = computed(() => {
      return store.state.quoteRequest.hasFailed
          && !store.state.quoteRequest.hasSucceeded;
    });

    /**
     * Indicates whether the quote request has succeeded.
     */
    const hasQuoteRequestSucceeded: ComputedRef<boolean> = computed(() => {
      return !store.state.quoteRequest.hasFailed
          && store.state.quoteRequest.hasSucceeded;
    });

    /**
     * Dispatches the actual QuoteRequestAction with the current @{quoteRequestInfo}
     */
    function dispatchQuoteRequest(): void {
      if (!quoteFormValid.value) {
        return;
      }

      store.dispatch(Actions.QuoteRequestAction, quoteRequestInfo.value);
      resetForm();
    }

    function handleQuoteModalOk(): void {
      dispatchQuoteRequest();
    }

    /**
     * Indicates whether the quote request has failed.
     */
    const hasQuoteRequestFinished: ComputedRef<boolean> = computed(() => {
      return !store.state.quoteRequest.isInProgress
          && (store.state.quoteRequest.hasSucceeded || store.state.quoteRequest.hasFailed);
    });


    /**
     * Returns the morticians name for display
     */
    const morticianName: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings.name;
    });

    // eslint-disable-next-line
    const printOptions: ComputedRef<any> = computed(() => {
      return {
        id: 'print-result',
        popTitle: 'Ihre Bestattungskonfiguration',
        extraHead: `<meta http-equiv="Content-Language" content="de-DE"/>,
                    <style>
                    html, body {
                        background-color: white!important;
                        color: black;
                    }
                    </style>`
      };
    });

    /**
     * Resets the callback info
     */
    function resetCallbackForm(): void {
      callbackFormData.value = {
        name: '',
        phoneNumber: '',
        preferredTime: ''
      };
    }

    function resetForm(): void {
      quoteRequestInfo.value = createEmptyQuoteRequestInfo();
    }

    /**
     * Checks the form validity and then dispatches a @{Actions.CallbackRequestAction}.
     */
    function callbackFormSubmit(): void {
      if (!isFormValid.value) {
        return;
      }
      store.dispatch(Actions.CallbackRequestAction, callbackFormData.value);
      resetCallbackForm();
    }

    function handleModalOk(): void {
      callbackFormSubmit();
    }

    onBeforeMount(() => {      // => Setup page/view change events
      resetForm();
      resetCallbackForm();
    });

    return {
      handleModalOk,
      callbackFormSubmit,
      resetCallbackForm,
      printOptions,
      morticianName,
      hasCallbackRequestFailed,
      hasCallbackRequestSucceeded,
      hasCallbackRequestFinished,
      isCallbackRequestInProgress,
      isQuoteRequestInProgress,
      hasQuoteRequestFailed,
      hasQuoteRequestSucceeded,
      hasQuoteRequestFinished,
      dispatchQuoteRequest,
      handleQuoteModalOk,
      quoteFormValid,
      quoteRequestInfo,
      callbackFormData,
      isFormValid
    };
  }
});
